import {
  type CrmActivity,
  type CrmContact,
  type CrmOpportunity,
  mockCrmActivities,
  mockCrmContacts,
  mockCrmOpportunities,
} from '../mocks/crm';
import { STORAGE_KEYS, readStoredJSON, writeStoredJSON } from './state/appState';

const DEMO_CONTACT_IDS = new Set(mockCrmContacts.map((contact) => contact.id));
const DEMO_OPPORTUNITY_IDS = new Set(mockCrmOpportunities.map((opportunity) => opportunity.id));
const DEMO_ACTIVITY_IDS = new Set(mockCrmActivities.map((activity) => activity.id));

function normalizeOpportunityCurrency(currency?: string): CrmOpportunity['currency'] {
  if (currency === 'EUR' || currency === 'TRY') return currency;
  return 'USD';
}

export function sanitizeCrmContacts(contacts: CrmContact[]) {
  return contacts.filter((contact) => !DEMO_CONTACT_IDS.has(contact.id));
}

export function sanitizeCrmOpportunities(opportunities: CrmOpportunity[]) {
  return opportunities
    .filter(
      (opportunity) =>
        !DEMO_OPPORTUNITY_IDS.has(opportunity.id) &&
        !DEMO_CONTACT_IDS.has(opportunity.contactId),
    )
    .map((opportunity) => ({
      ...opportunity,
      currency: normalizeOpportunityCurrency(opportunity.currency),
    }));
}

export function sanitizeCrmActivities(activities: CrmActivity[]) {
  return activities.filter(
    (activity) =>
      !DEMO_ACTIVITY_IDS.has(activity.id) &&
      !DEMO_CONTACT_IDS.has(activity.contactId) &&
      !(activity.opportunityId && DEMO_OPPORTUNITY_IDS.has(activity.opportunityId)),
  );
}

export function loadStoredCrmState() {
  return {
    opportunities: sanitizeCrmOpportunities(
      readStoredJSON<CrmOpportunity[]>(STORAGE_KEYS.crmOpportunities, []),
    ),
    contacts: sanitizeCrmContacts(readStoredJSON<CrmContact[]>(STORAGE_KEYS.crmContacts, [])),
    activities: sanitizeCrmActivities(
      readStoredJSON<CrmActivity[]>(STORAGE_KEYS.crmActivities, []),
    ),
  };
}

export function saveStoredCrmState(input: {
  opportunities: CrmOpportunity[];
  contacts: CrmContact[];
  activities: CrmActivity[];
}) {
  writeStoredJSON(STORAGE_KEYS.crmOpportunities, sanitizeCrmOpportunities(input.opportunities));
  writeStoredJSON(STORAGE_KEYS.crmContacts, sanitizeCrmContacts(input.contacts));
  writeStoredJSON(STORAGE_KEYS.crmActivities, sanitizeCrmActivities(input.activities));
}

export function sanitizeLegacyCrmDataInStorage() {
  if (typeof window === 'undefined') return false;

  const next = loadStoredCrmState();
  const writes: Array<[string, string]> = [
    [STORAGE_KEYS.crmOpportunities, JSON.stringify(next.opportunities)],
    [STORAGE_KEYS.crmContacts, JSON.stringify(next.contacts)],
    [STORAGE_KEYS.crmActivities, JSON.stringify(next.activities)],
  ];

  let didChange = false;

  writes.forEach(([key, payload]) => {
    if (window.localStorage.getItem(key) !== payload) {
      window.localStorage.setItem(key, payload);
      didChange = true;
    }
  });

  return didChange;
}
